'use client'

import type {
  FitchRating,
  MoodysRating,
  SPRating,
} from '@moment/trading-types/reference-data'
import type { ICellRendererParams } from 'ag-grid-community'
import type React from 'react'

import { BodyMediumSubtext } from '@/components/atomic/text'
import { RatingBadge } from '@/components/molecules/badges/ratings'
import { useReferenceDataPermissions } from '@/hooks/use-reference-data-permissions'
import { PLACEHOLDER } from '@/utils/presentation/formatting'

/**
 * Rating cell renderer component that automatically checks permissions
 * Now also handles average ratings
 */
export const RatingCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const { hasSPAccess, hasMoodysAccess, hasFitchAccess, isLoading } =
    useReferenceDataPermissions()

  const isAverageRating =
    params.colDef?.cellRendererParams?.isAverageRating || false

  let finalSPRating: string | undefined
  let finalMoodysRating: string | undefined
  let finalFitchRating: string | undefined

  if (isAverageRating) {
    const { average_sp_rating, average_moodys_rating } = params.data || {}
    finalSPRating = average_sp_rating
    finalMoodysRating = average_moodys_rating
  } else {
    const { spRating, sp_rating, moodys_rating, moodysRating, fitch_rating } =
      params.data || {}
    const legacySpRating = params.value

    finalSPRating = spRating || sp_rating || legacySpRating
    finalMoodysRating = moodysRating || moodys_rating
    finalFitchRating = fitch_rating
  }

  if (isLoading) {
    const hasAnyRating = finalSPRating || finalMoodysRating || finalFitchRating
    return hasAnyRating ? (
      <BodyMediumSubtext>
        <RatingBadge
          spRating={finalSPRating as SPRating}
          moodysRating={finalMoodysRating as MoodysRating}
          fitchRating={finalFitchRating as FitchRating}
          spAccess={hasSPAccess ?? false}
          moodysAccess={hasMoodysAccess ?? false}
          fitchAccess={hasFitchAccess ?? false}
        />
      </BodyMediumSubtext>
    ) : (
      <BodyMediumSubtext>{PLACEHOLDER}</BodyMediumSubtext>
    )
  }

  // Filter ratings based on permissions
  const displaySPRating = hasSPAccess ? finalSPRating : null
  const displayMoodysRating = hasMoodysAccess ? finalMoodysRating : null
  const displayFitchRating = hasFitchAccess ? finalFitchRating : null

  return displaySPRating || displayMoodysRating || displayFitchRating ? (
    <BodyMediumSubtext>
      <RatingBadge
        spRating={displaySPRating as SPRating}
        moodysRating={displayMoodysRating as MoodysRating}
        fitchRating={displayFitchRating as FitchRating}
        spAccess={hasSPAccess ?? false}
        moodysAccess={hasMoodysAccess ?? false}
        fitchAccess={hasFitchAccess ?? false}
      />
    </BodyMediumSubtext>
  ) : (
    <BodyMediumSubtext>{PLACEHOLDER}</BodyMediumSubtext>
  )
}
